// src/context/AuthContext.js — Tracks the Supabase session and exposes auth actions
import { createContext, useContext, useEffect, useState } from 'react'
import supabase from '../lib/supabase'
import api from '../lib/api'
import { clearSwitchGrant } from '../lib/accountSwitchGrant'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user,    setUser]    = useState(null)
  const [profile, setProfile] = useState(null)   // this app's users row: { role, contact_id, ... }
  const [loading, setLoading] = useState(true)

  // Fetch the app-level profile (role, contact_id) once a session exists.
  // Drives role-based routing — see ProtectedRoute.
  async function loadProfile() {
    try {
      const response = await api.get('/api/users/me')
      setProfile(response.data.data)
    } catch {
      setProfile(null)
    }
  }

  useEffect(() => {
    let mounted = true

    // Restore an existing session on mount (e.g. page refresh).
    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return
      setSession(data.session ?? null)
      setUser(data.session?.user ?? null)
      if (data.session?.user) await loadProfile()
      if (mounted) setLoading(false)
    })

    // Keep state in sync with Supabase auth changes (sign-in, sign-out, refresh).
    // loading is set true for the duration of this handler — otherwise, once
    // the initial mount has already resolved loading=false (no session yet),
    // a sign-in event would set `user` synchronously while `profile` is still
    // null, and ProtectedRoute would briefly render with the wrong role
    // (profile null → isPartner false) before loadProfile() resolves.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      // A reset link normally lands on /set-password already (redirectTo);
      // this catches one that falls back to the Site URL root instead.
      if (event === 'PASSWORD_RECOVERY' && window.location.pathname !== '/set-password') {
        window.location.assign('/set-password')
        return
      }
      setLoading(true)
      setSession(newSession ?? null)
      setUser(newSession?.user ?? null)
      ;(async () => {
        if (newSession?.user) {
          await loadProfile()
        } else {
          setProfile(null)
        }
        if (mounted) setLoading(false)
      })()
    })

    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  async function signOut() {
    clearSwitchGrant()
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
